import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Space, Spin, Table, Tabs } from "antd";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import { loadEntete, loadLogos, drawEtatHeaderFooter, finalizeEtatPdf, NAVY } from "../../stats/sinistre/etatsSinistresFunctions";
import axiosInstance from "../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../variables/numbersFunctions";

// Les 7 rubriques du drill-down, IDENTIQUES au dashboard /reseau/suivi-agences (mêmes clés
// `type` attendues par GET /api/agence/{code}/listing?type=).
const LISTING_TABS = [
    { key: "CA", label: "Production", field: "montant", color: "#242166" },
    { key: "ENCAISSEES", label: "Primes encaissées", field: "montant", color: "#16a34a" },
    { key: "NON_ENCAISSEES", label: "Primes non encaissées", field: "reste", color: "#d97706" },
    { key: "REVERSEES", label: "Primes reversées", field: "montant", color: "#16a34a" },
    { key: "NON_REVERSEES", label: "Primes non reversées", field: "montant", color: "#dc2626" },
    { key: "COMM_DUES", label: "Commissions dues", field: "commission", color: "#d97706" },
    { key: "COMM_ACQUISES", label: "Commissions payées", field: "commission", color: "#16a34a" },
];

const fcfa = (v) => `${displayNumbers(Math.trunc(v || 0))} F`;
const trunc = (v) => Math.trunc(v || 0);
const pdfNum = (v) => new Intl.NumberFormat("en-DE").format(Math.floor(v || 0)).replace(/\./g, " ");
const fileStamp = () => new Date().toISOString().slice(0, 10);

/**
 * Modale de listings d'une AGENCE (mêmes états que /reseau/suivi-agences) : production,
 * primes encaissées / non encaissées / reversées / non reversées, commissions dues / payées.
 * Chaque rubrique est exportable en Excel et en PDF. Source : GET /api/agence/{codeAgence}/listing.
 */
const AgenceListingsModal = ({ open, onClose, codeAgence, codeOrganisme, nomAgence, periode, initialTab = "ENCAISSEES" }) => {
    const [tab, setTab] = useState(initialTab);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => { if (open) setTab(initialTab || "ENCAISSEES"); }, [open, initialTab]);

    useEffect(() => {
        if (!open || !codeAgence) return;
        setLoading(true);
        const params = { type: tab };
        if (codeOrganisme) params.codeOrganisme = codeOrganisme;
        if (periode?.length === 2 && periode[0] && periode[1]) {
            params.start = periode[0].format("YYYY-MM-DD");
            params.end = periode[1].format("YYYY-MM-DD");
        }
        axiosInstance.get(`/api/agence/${codeAgence}/listing`, { params })
            .then((r) => setData(Array.isArray(r.data) ? r.data.map((row, i) => ({ ...row, __rowKey: i })) : []))
            .catch(() => setData([]))
            .finally(() => setLoading(false));
    }, [open, codeAgence, codeOrganisme, tab, periode]);

    const tabDef = LISTING_TABS.find((t) => t.key === tab) || LISTING_TABS[0];
    const total = useMemo(
        () => data.reduce((s, r) => s + trunc(Number(r[tabDef.field]) || 0), 0),
        [data, tabDef.field]
    );

    const columns = useMemo(() => {
        const base = [
            { title: "N° Police", dataIndex: "numeroPolice", key: "np", render: (v) => v || "—" },
            { title: "Souscripteur", dataIndex: "souscripteur", key: "s", render: (v) => v || "—" },
            { title: "Catégorie", dataIndex: "categorie", key: "c", render: (v) => v || "—" },
            { title: "Av.", dataIndex: "avenant", key: "av", align: "center", render: (v) => v || "—" },
            { title: "Date", dataIndex: "date", key: "d", render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—") },
        ];
        if (tab === "NON_ENCAISSEES") {
            base.push({ title: "Prime", dataIndex: "montant", key: "m", align: "right", render: (v) => fcfa(v) });
            base.push({ title: "Reste à encaisser", dataIndex: "reste", key: "r", align: "right", render: (v) => <span style={{ color: "#d97706" }}>{fcfa(v)}</span> });
        } else if (tab === "COMM_DUES" || tab === "COMM_ACQUISES") {
            base.push({ title: "Commission", dataIndex: "commission", key: "cm", align: "right", render: (v) => <strong style={{ color: tabDef.color }}>{fcfa(v)}</strong> });
        } else if (tab === "CA" || tab === "ENCAISSEES") {
            base.push(
                { title: "PN Compagnie", dataIndex: "primeNetteCompagnie", key: "pnc", align: "right", render: (v) => fcfa(v) },
                { title: "PN Pool", dataIndex: "primeNettePool", key: "pnp", align: "right", render: (v) => fcfa(v) },
                { title: "CP", dataIndex: "accessoires", key: "cp", align: "right", render: (v) => fcfa(v) },
                { title: "Taxes", dataIndex: "taxes", key: "tx", align: "right", render: (v) => fcfa(v) },
                { title: "PTTC", dataIndex: "primeTotale", key: "pttc", align: "right", render: (v) => <strong style={{ color: tabDef.color }}>{fcfa(v)}</strong> },
            );
        } else {
            base.push({ title: "Montant", dataIndex: "montant", key: "m", align: "right", render: (v) => <strong style={{ color: tabDef.color }}>{fcfa(v)}</strong> });
        }
        return base;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const exportExcel = () => {
        const rows = data.map((r) => ({
            "N° Police": r.numeroPolice || "",
            "Souscripteur": r.souscripteur || "",
            "Catégorie": r.categorie || "",
            "Avenant": r.avenant || "",
            "Date": r.date ? dayjs(r.date).format("DD/MM/YYYY") : "",
            ...((tab === "CA" || tab === "ENCAISSEES")
                ? { "PN Compagnie": trunc(r.primeNetteCompagnie), "PN Pool": trunc(r.primeNettePool), "CP": trunc(r.accessoires), "Taxes": trunc(r.taxes), "PTTC": trunc(r.primeTotale) }
                : { "Montant": trunc(r.montant) }),
            ...(tab === "NON_ENCAISSEES" ? { "Reste à encaisser": trunc(r.reste) } : {}),
            ...((tab === "COMM_DUES" || tab === "COMM_ACQUISES") ? { "Commission": trunc(r.commission) } : {}),
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Listing");
        const name = (nomAgence || codeAgence || "agence").replace(/[^\w-]+/g, "_");
        XLSX.writeFile(wb, `${name}-${tab.toLowerCase()}-${fileStamp()}.xlsx`);
    };

    const exportPDF = async () => {
        if (!data.length) return;
        const doc = new jsPDF({ orientation: "portrait" });
        let enteteData = null, logos = [];
        try { [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]); } catch (e) { /* optionnels */ }
        const formattedNow = new Date().toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
        const isNonEnc = tab === "NON_ENCAISSEES";
        const isComm = tab === "COMM_DUES" || tab === "COMM_ACQUISES";
        const isVentile = tab === "CA" || tab === "ENCAISSEES";
        const head = [["N° Police", "Souscripteur", "Catégorie", "Av.", "Date",
            ...(isVentile ? ["PN Comp.", "PN Pool", "CP", "Taxes", "PTTC"]
                : isNonEnc ? ["Prime", "Reste à enc."]
                    : isComm ? ["Commission"] : ["Montant"])]];
        const body = data.map((r) => [
            r.numeroPolice || "", r.souscripteur || "", r.categorie || "", r.avenant || "",
            r.date ? dayjs(r.date).format("DD/MM/YYYY") : "",
            ...(isVentile
                ? [pdfNum(r.primeNetteCompagnie), pdfNum(r.primeNettePool), pdfNum(r.accessoires), pdfNum(r.taxes), pdfNum(r.primeTotale)]
                : isNonEnc ? [pdfNum(r.montant), pdfNum(r.reste)]
                    : isComm ? [pdfNum(r.commission)] : [pdfNum(r.montant)]),
        ]);
        const nCols = 5 + (isVentile ? 5 : isNonEnc ? 2 : 1);
        const foot = [[
            { content: "TOTAL", colSpan: nCols - 1, styles: { halign: "right", fontStyle: "bold" } },
            { content: pdfNum(total), styles: { halign: "right", fontStyle: "bold" } },
        ]];
        const periodeLabel = periode?.length === 2 && periode[0] && periode[1]
            ? ` (${periode[0].format("DD/MM/YYYY")} au ${periode[1].format("DD/MM/YYYY")})` : "";
        autoTable(doc, {
            startY: 34, margin: { top: 32, bottom: 20, left: 14, right: 14 }, head, body, foot,
            styles: { fontSize: 8, cellPadding: 1.8 }, headStyles: { fillColor: NAVY, fontSize: 8 },
            footStyles: { fillColor: [243, 244, 246], textColor: 20 },
            columnStyles: Object.fromEntries(Array.from({ length: nCols - 5 }, (_, i) => [5 + i, { halign: "right" }])),
            didDrawPage: () => drawEtatHeaderFooter(doc, {
                title: `${(nomAgence || codeAgence || "").toUpperCase()} — ${tabDef.label.toUpperCase()}`,
                subtitle: `${data.length} ligne(s) · Total ${pdfNum(total)} F${periodeLabel}`,
                enteteData, logos, formattedNow,
            }),
        });
        finalizeEtatPdf(doc);
        const name = (nomAgence || codeAgence || "listing").replace(/[^\w-]+/g, "_");
        doc.save(`${name}-${tab.toLowerCase()}-${fileStamp()}.pdf`);
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={1080}
            footer={null}
            title={`États de l'agence — ${nomAgence || codeAgence || ""}`}
        >
            <Tabs
                activeKey={tab}
                onChange={setTab}
                items={LISTING_TABS.map((t) => ({ key: t.key, label: t.label }))}
                tabBarExtraContent={
                    <Space>
                        <Button size="small" icon={<FileExcelOutlined />} onClick={exportExcel} disabled={!data.length}>Excel</Button>
                        <Button size="small" danger icon={<FilePdfOutlined />} onClick={exportPDF} disabled={!data.length}>PDF</Button>
                    </Space>
                }
            />
            <Spin spinning={loading}>
                <Table
                    size="small"
                    rowKey="__rowKey"
                    columns={columns}
                    dataSource={data}
                    pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} ligne(s)` }}
                    scroll={{ x: "max-content" }}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={columns.length - 1} align="right">
                                    <strong>TOTAL</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">
                                    <strong style={{ color: tabDef.color }}>{fcfa(total)}</strong>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            </Spin>
        </Modal>
    );
};

export default AgenceListingsModal;
